import { ENTITY_LINK_TYPES, type EntityLinkType } from './entityLinks';

/**
 * The entities an admin can search for and pick by name, rather than by
 * remembering an id.
 *
 * This is deliberately a WIDER set than `EntityLinkType`, and the two must not
 * be merged. `EntityLinkType` is a wire contract: it names the entities a
 * curator can link to from inside a rich-text body, it is hand-mirrored in
 * watchlock's `utils/entityLinks.ts`, and every value in it has to be
 * something the RN renderer will meet in authored prose. Adding "electronic
 * module" to that set would widen a cross-repo contract to serve a dropdown.
 *
 * What the push and banner composers need is a different question — "which
 * things can I search for by name" — and it has a different answer. Nobody
 * writes "as the 6P29 module shows" mid-paragraph, but an admin absolutely
 * wants to send a broadcast about that module without looking its id up first.
 *
 * So: one picker component, two vocabularies. The rich-text editor passes
 * `ENTITY_LINK_TYPES`; the composers pass `DEEP_LINK_PICKABLE_TYPES`.
 */
export type PickableEntityType =
  | EntityLinkType
  | 'manufacturer'
  | 'module'
  | 'collection';

/**
 * Compile-time proof that every rich-text entity type is also pickable, so the
 * picker can serve both callers. If someone adds a fifth `EntityLinkType`
 * without teaching the picker about it, this line stops the build rather than
 * letting a tab render an empty result list.
 */
const _entityLinkTypesArePickable: readonly PickableEntityType[] =
  ENTITY_LINK_TYPES;
void _entityLinkTypesArePickable;

/** Tab labels. The rich-text editor keeps its own for the four it offers. */
export const PICKABLE_ENTITY_LABELS: Record<PickableEntityType, string> = {
  reference: 'Reference',
  brand: 'Brand',
  calibre: 'Calibre',
  article: 'Article',
  manufacturer: 'Manufacturer',
  'parent-org': 'Parent organisation',
  module: 'Module',
  collection: 'Collection',
};

export const PICKABLE_ENTITY_PLACEHOLDERS: Record<PickableEntityType, string> = {
  reference: 'Search by reference number, model, or brand…',
  brand: 'Search brands…',
  calibre: 'Search calibres by name or AKA…',
  article: 'Search articles by title…',
  manufacturer: 'Search movement manufacturers…',
  'parent-org': 'Search parent organisations…',
  module: 'Search electronic modules by name…',
  collection: 'Search curated collections by name…',
};

/**
 * The types the deep-link composers offer, in the order they appear.
 *
 * Labels here match what the admin already called these things — the calibre
 * tab stays "Calibre", the word this codebase settled on. Only the deep-link
 * dropdown says "Movement (calibre)", because that is the entry an admin scans
 * for when they are thinking about the app's Explore section rather than about
 * the catalogue.
 */
export const DEEP_LINK_PICKABLE_TYPES: readonly PickableEntityType[] = [
  'reference',
  'brand',
  'calibre',
  'manufacturer',
  'module',
  'article',
  'collection',
] as const;
